import React, { useEffect, useState, useMemo, useCallback } from 'react'
import Box from '@mui/material/Box'
import { editObject, getObject, addObject } from '../../../api/apiClientCrud'
import useNavigate from '../../hooks/useNavigate'
import { useAuth } from '../../../context/AuthContext'
import { useLocation } from 'react-router-dom'
import Form from '../../form/Form'
import { getProductFields } from './ProductFields'
import useSaveEntity from '../../hooks/useSaveEntity'
import { useFetchEntities } from '../../hooks/useFetchEntities'
import Toolbar from '../../toolbar/Toolbar'
import { handleProductImagesUpload } from '../../../utils/documentManager'

const ROUTES = {
  EDIT: '/products/edit',
  VIEW: '/products/view',
  ADD: '/products/add'
}

const isPreviewAttachment = (attachment) => attachment?.type === 'image' && (attachment?.isPreview === true || attachment?.file instanceof File)
const getPreviewFileKey = (file) => `${file?.name || ''}-${file?.size || 0}-${file?.lastModified || 0}`

const getImagePosition = (attachment, fallbackIndex) => {
  const parsed = Number(attachment?.position)
  return Number.isFinite(parsed) ? parsed : fallbackIndex + 1
}

const sortImageAttachmentsInPlace = (attachments = []) => {
  const sortedImages = attachments
    .filter((attachment) => attachment?.type === 'image')
    .sort((left, right) => getImagePosition(left, 0) - getImagePosition(right, 0))

  let imageIndex = 0

  return attachments.map((attachment) => {
    if (attachment?.type !== 'image') return attachment
    return sortedImages[imageIndex++]
  })
}

const normalizeImageAttachmentOrder = (attachments = []) => {
  let position = 1

  return attachments.map((attachment) => {
    if (attachment?.type !== 'image') return attachment

    return {
      ...attachment,
      position: position++
    }
  })
}

const prepareProductAttachments = (attachments = []) => normalizeImageAttachmentOrder(sortImageAttachmentsInPlace(attachments))

const mergeUploadedImagesInOrder = (attachments = [], uploadedImages = []) => {
  let uploadedIndex = 0

  const mergedAttachments = attachments.reduce((acc, attachment) => {
    if (!isPreviewAttachment(attachment)) {
      acc.push(attachment)
      return acc
    }

    const uploadedImage = uploadedImages[uploadedIndex]
    uploadedIndex += 1

    if (uploadedImage) acc.push(uploadedImage)

    return acc
  }, [])

  return prepareProductAttachments([...mergedAttachments, ...uploadedImages.slice(uploadedIndex)])
}

const buildPayload = ({ data, user, mode }) => {
  const isEdit = mode === 'edit'

  const sanitizedData = {
    ...data,
    ...(data?.customer_type === 2 ? { siret: null, siren: null } : {})
  }

  return {
    ...sanitizedData,
    ...(isEdit
      ? { lastModBy: user.email }
      : {
          owner_id: user.company_id,
          company_id: user.company_id,
          createdBy: user.email
        })
  }
}

const EditProduct = () => {
  const [product, setProduct] = useState({ options: [] })
  const [isSubmit, setIsSubmit] = useState(false)
  const history = useNavigate()
  const { user } = useAuth()
  const location = useLocation()
  const path = location.pathname
  const productId = location.state?.id

  const mode = useMemo(() => {
    if (path === ROUTES.EDIT) return 'edit'
    if (path === ROUTES.VIEW) return 'view'
    if (path === ROUTES.ADD) return 'add'
    return null
  }, [path])

  // ✅ SIMPLE: Juste une fonction qui met à jour product.options
  // MyTableEditable gère toute la complexité en interne
  const setProductOptions = useCallback((payload) => {
    setProduct((prev) => {
      const currentOptions = Array.isArray(prev.options) ? prev.options : []

      // Support updater function from MyTableEditable, which receives the table rows.
      if (typeof payload === 'function') {
        const result = payload(currentOptions)
        return Array.isArray(result) ? { ...prev, options: result } : { ...prev, ...result }
      }

      // Support direct array
      if (Array.isArray(payload)) {
        return { ...prev, options: payload }
      }

      // Support object with .options
      if (payload && payload.options) {
        return { ...prev, options: payload.options }
      }

      return prev
    })
  }, [])

  const productQuery = mode === 'edit' ? { key: 'product', fn: getObject, args: ['products', productId] } : null

  const queries = [productQuery].filter(Boolean)

  const { data: fetched, loading: loadingEntities } = useFetchEntities(queries)

  const { saveEntity, loading } = useSaveEntity({
    entityName: 'products',
    mode,
    id: productId,
    user,
    addFn: addObject,
    editFn: editObject,
    updateCounter: false,
    onSuccessRedirect: (id) => history(location.state?.history || ROUTES.VIEW, { state: { id } }),
    onSuccess: () => setIsSubmit(false),
    onError: () => setIsSubmit(false),
    buildPayload
  })

  const memoizedFields = useMemo(() => getProductFields(product, setProductOptions), [product, setProductOptions])

  const options = useMemo(() => ({ fields: memoizedFields, data: product }), [memoizedFields, product])

  const toolbarOptions = useMemo(
    () => ({
      title: () => {
        return path.includes('/add') ? 'Nouveau Produit' : `Produit ${product.name}`
      }
    }),
    [product, path]
  )

  useEffect(() => {
    if (!loadingEntities) {
      if (fetched.product) {
        setProduct({
          ...fetched.product,
          attachments: prepareProductAttachments(fetched.product.attachments || [])
        })
      }
    }
  }, [fetched, loadingEntities])

  useEffect(() => {
    const uploadAndSave = async () => {
      let productToSave = {
        ...product,
        attachments: prepareProductAttachments(product.attachments || [])
      }
      const previewAttachments = (product.attachments || []).filter(isPreviewAttachment)
      const filesToUpload = previewAttachments.map((attachment) => attachment.file).filter(Boolean)

      if (filesToUpload.length > 0 && product?.id) {
        const uploadedImages = await handleProductImagesUpload(filesToUpload, user, product)

        if (uploadedImages.length === filesToUpload.length) {
          productToSave = {
            ...productToSave,
            attachments: mergeUploadedImagesInOrder(productToSave.attachments || [], uploadedImages)
          }
        }
      }

      setProduct(productToSave)
      saveEntity({ data: productToSave })
    }

    if (isSubmit) {
      uploadAndSave()
    }
  }, [isSubmit])

  useEffect(() => {
    if (product.file && product.file.length > 0) {
      setProduct((prev) => {
        const existingAttachments = prev.attachments || []
        const existingPreviewKeys = new Set(
          existingAttachments
            .filter((attachment) => attachment?.isPreview && attachment?.file)
            .map((attachment) => getPreviewFileKey(attachment.file))
        )

        const newPreviews = Array.from(product.file)
          .filter((file) => !existingPreviewKeys.has(getPreviewFileKey(file)))
          .map((file) => ({
            type: 'image',
            file,
            isPreview: true
          }))

        return {
          ...prev,
          attachments: prepareProductAttachments([...existingAttachments, ...newPreviews]),
          file: undefined
        }
      })
    }
  }, [product.file])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        overflowY: 'auto',
        flexBasis: 'auto',
        flexGrow: 1,
        flexShrink: 1
      }}
    >
      <Toolbar options={toolbarOptions} object={product} />

      <Form
        mode='edit'
        options={options}
        setDataParent={setProduct}
        setIsSubmitParent={setIsSubmit}
        loading={loading}
      />
    </Box>
  )
}

export default EditProduct
